import React, { useState, useRef, useEffect } from 'react';
import gql from 'graphql-tag';
import { useStoreState } from 'easy-peasy';
import apolloClient from '../../../../utils/apolloClient';
import styles from './PlaceGate.module.css';
import { formatCurrency } from '../utils';

// ─── Live search (search_places) ─────────────────────────────────────────────

const SEARCH_PLACES = gql`
  query SearchPlacesForGate($input: SearchInput) {
    search_places(input: $input) {
      place_listing {
        place_id
        name
        city
        state
      }
    }
  }
`;

const fetchPlaceSuggestions = async (userId, query) => {
  try {
    const { data } = await apolloClient.clientPrivate.query({
      query: SEARCH_PLACES,
      variables: {
        input: {
          user_id: userId,
          from: 0,
          size: 10,
          filter: { place_filter: { status: 'ACTIVE', place_name: query } },
        },
      },
      fetchPolicy: 'network-only',
    });
    return data?.search_places?.place_listing ?? [];
  } catch {
    return [];
  }
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <circle cx="6.5" cy="6.5" r="5" stroke="#B9AFA8" strokeWidth="1.5" />
    <path d="M11 11L14.5 14.5" stroke="#B9AFA8" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

const PlaceGate = ({ places, onSelectPlace, onSelectSuggestion, isLoading }) => {
  const { userId } = useStoreState(state => state.auth);

  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onClickOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleQueryChange = e => {
    const { value } = e.target;
    setInputValue(value);

    clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setSuggestions([]);
      setSearching(false);
      setDropdownOpen(false);
      return;
    }

    setSearching(true);
    setDropdownOpen(true);
    debounceRef.current = setTimeout(async () => {
      const results = await fetchPlaceSuggestions(userId, value);
      setSuggestions(results);
      setSearching(false);
    }, 300);
  };

  const handleSuggestionSelect = place => {
    onSelectSuggestion(place.place_id);
    setInputValue(place.name);
    setSuggestions([]);
    setDropdownOpen(false);
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setDropdownOpen(false);
    clearTimeout(debounceRef.current);
  };

  return (
    <div className={styles.gate}>
      <div className={styles.header}>
        <div className={styles.title}>Select a Place to View Payments</div>
        <div className={styles.subtitle}>
          Choose a venue to view their payment history, invoices and order transactions
        </div>

        <div className={styles.searchWrapper} ref={wrapperRef}>
          <div className={styles.searchBar}>
            <SearchIcon />
            <input
              className={styles.searchInput}
              placeholder="Search place name…"
              value={inputValue}
              onChange={handleQueryChange}
              autoComplete="off"
            />
            {searching && <div className={styles.searchSpinner} />}
            {!searching && inputValue && (
              <button className={styles.clearBtn} onClick={handleClear}>✕</button>
            )}
          </div>

          {dropdownOpen && (
            <div className={styles.dropdown}>
              {searching && (
                <div className={styles.dropdownItem} style={{ color: '#b9afa8', cursor: 'default' }}>
                  Searching…
                </div>
              )}
              {!searching && suggestions.length === 0 && (
                <div className={styles.dropdownItem} style={{ color: '#b9afa8', cursor: 'default' }}>
                  No places found
                </div>
              )}
              {!searching && suggestions.map(p => (
                <div
                  key={p.place_id}
                  className={styles.dropdownItem}
                  onMouseDown={() => handleSuggestionSelect(p)}
                >
                  <span className={styles.dropdownName}>{p.name}</span>
                  <span className={styles.dropdownMeta}>{p.city}, {p.state}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.body}>
        {isLoading ? (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>⏳</div>
            <div className={styles.emptyTitle}>Loading places…</div>
          </div>
        ) : (
          <>
            <div className={styles.sectionTitle}>
              {places.length} Place{places.length !== 1 ? 's' : ''} available
            </div>

            {places.length === 0 ? (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>🏪</div>
                <div className={styles.emptyTitle}>No places found</div>
                <div className={styles.emptySub}>No invoice data available yet</div>
              </div>
            ) : (
              places.map(p => (
                <div
                  key={p.placeId}
                  className={styles.placeCard}
                  onClick={() => onSelectPlace(p.placeId)}
                >
                  <div className={styles.placeLeft}>
                    <div className={styles.placeName}>{p.name}</div>
                    <div className={styles.placeMeta}>{p.city}, {p.state}</div>
                  </div>
                  <div className={styles.placeRight}>
                    <div className={styles.placeYtd}>{formatCurrency(p.netPayout)}</div>
                    <div className={styles.placeYtdLabel}>Net payout</div>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlaceGate;
